import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * Who the current visitor is.
 *
 * /session is the one endpoint that does not 401 when signed out, so this can
 * load on first paint without the signed-out case looking like an error.
 */
export function useSession() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/session')
      .then((s) => { if (!cancelled) { setSession(s); setReady(true); } })
      .catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  const signOut = useCallback(async () => {
    // DELETE rather than a link to /auth/logout: navigating away lands the
    // person on the API host, and a link is a GET, so anything that prefetches
    // one could sign them out. This carries the CSRF header and leaves them
    // looking at the app's own signed-out screen.
    await api.delete('/session');
    // Keep what the server said it offers: the signed-out screen draws its
    // buttons from it, and re-fetching /session to learn it again would flash
    // a screen with no way to sign in.
    setSession((current) => ({ signed_in: false, providers: current?.providers }));
  }, []);

  return {
    session,
    ready,
    signedIn: Boolean(session?.signed_in),
    // Which sign-in providers the server offers. Only sent when signed out.
    providers: session?.providers ?? [],
    canEdit: Boolean(session?.can_edit),
    canComment: Boolean(session?.can_comment),
    // The gate and the access list: how Hyphen runs the service, which only an
    // editor has any part in.
    seesOperations: Boolean(session?.sees_operations),
    role: session?.role ?? '',
    email: session?.email ?? '',
    signOut,
  };
}

/**
 * Load something from the API once, with the loading and error states every
 * view otherwise repeats.
 */
export function useResource(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    try {
      setData(await api.get(path));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [path]);

  useEffect(() => {
    let cancelled = false;
    api.get(path)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  return { data, error, setData, setError, reload };
}
